import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import { FileText, Download, Search, ArrowLeft, User, GraduationCap, Award } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getToken } from "../../utils/auth";

export default function EvaluatorMarkingDetails() {
  const { evaluatorId, programId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [markingDetails, setMarkingDetails] = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMarkingDetails();
  }, [evaluatorId, programId]);

  useEffect(() => {
    filterDetails();
  }, [searchTerm, markingDetails]);

  const fetchMarkingDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroadSelectionMarking/marking-details`,
        {
          params: {
            programId: programId,
            evaluatorId: evaluatorId,
          },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setMarkingDetails(response.data || []);
      setFilteredDetails(response.data || []);
    } catch (err) {
      setError("Failed to fetch marking details");
      toast.error("Failed to load marking details");
    } finally {
      setLoading(false);
    }
  };

  const filterDetails = () => {
    if (!searchTerm) {
      setFilteredDetails(markingDetails);
      return;
    }

    const filtered = markingDetails.filter(
      (detail) =>
        detail.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.nationalId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.criteriaName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredDetails(filtered);
  };

  const handleExportToExcel = () => {
    const excelData = filteredDetails.map((detail, index) => ({
      "#": index + 1,
      "First Name": detail.firstName || "",
      "Last Name": detail.lastName || "",
      "Email": detail.email || "",
      "National ID": detail.nationalId || "",
      "Phone": detail.phoneNumber || "",
      "Gender": detail.gender || "",
      "Current Level": detail.currentLevel || "",
      "Apply Level": detail.applyLevel || "",
      "Field of Study": detail.fieldOfStudy || "",
      "Priority Course": detail.priorityCourse || "",
      "Priority Level": detail.priorityLevel || "",
      "Academic Merit": detail.academicMerit || "",
      "Working Place": detail.workingPlace || "",
      "Start Date": detail.startDate || "",
      "End Date": detail.endDate || "",
      "Tuition Fee": detail.tuitionFee || "",
      "Monthly Living Allowance": detail.monthlyLivingAllowance || "",
      "Research Allowance": detail.researchAllowance || "",
      "Air Ticket Allowance": detail.airTicketAllowance || "",
      "Criteria Name": detail.criteriaName || "",
      "Marks Obtained": detail.marksObtained || "",
      "Weight Obtained": detail.weightObtained || "",
      "Comment": detail.comment || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Marking Details");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Evaluator_Marking_Details_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Marking details exported successfully!", {
      position: "top-center",
      autoClose: 3000,
      theme: "colored",
    });
  };

  // Group markings by application
  const groupByApplication = () => {
    const grouped = {};
    filteredDetails.forEach((detail) => {
      const key = detail.applicationId;
      if (!grouped[key]) {
        grouped[key] = {
          applicationId: detail.applicationId,
          firstName: detail.firstName,
          lastName: detail.lastName,
          email: detail.email,
          nationalId: detail.nationalId,
          phoneNumber: detail.phoneNumber,
          gender: detail.gender,
          currentLevel: detail.currentLevel,
          applyLevel: detail.applyLevel,
          fieldOfStudy: detail.fieldOfStudy,
          priorityCourse: detail.priorityCourse,
          priorityLevel: detail.priorityLevel,
          academicMerit: detail.academicMerit,
          workingPlace: detail.workingPlace,
          startDate: detail.startDate,
          endDate: detail.endDate,
          tuitionFee: detail.tuitionFee,
          monthlyLivingAllowance: detail.monthlyLivingAllowance,
          researchAllowance: detail.researchAllowance,
          airTicketAllowance: detail.airTicketAllowance,
          markings: [],
        };
      }
      grouped[key].markings.push({
        criteriaName: detail.criteriaName,
        marksObtained: detail.marksObtained,
        weightObtained: detail.weightObtained,
        comment: detail.comment,
      });
    });
    return Object.values(grouped);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <FileText className="w-8 h-8 text-hec-600" />
              </div>
              <p className="text-hec-600 text-lg">Loading marking details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const groupedApplications = groupByApplication();

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBarComponent />
      <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
        <SidebarComponent />
      </aside>

      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => navigate(-1)}
                      className="w-12 h-12 bg-hec-100 hover:bg-hec-200 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <ArrowLeft className="w-6 h-6 text-hec-700" />
                    </button>
                    <div className="w-14 h-14 bg-gradient-to-r from-hec-600 to-hec-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-hec-700">Evaluated Applications</h1>
                      <p className="text-hec-600 mt-1">
                        Detailed marking information for {groupedApplications.length} application(s)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleExportToExcel}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    <span>Export to Excel</span>
                  </button>
                </div>

                {/* Search */}
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search className="w-4 h-4 inline mr-2" />
                    Search Applications
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, ID, or criteria..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                  />
                </div>
              </div>
            </div>

            {/* Applications */}
            {groupedApplications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-12">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No evaluated applications found.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedApplications.map((app, index) => (
                  <div key={app.applicationId} className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                    {/* Application Header */}
                    <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-200">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-hec-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-hec-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-hec-700">
                            {app.firstName} {app.lastName}
                          </h2>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p><strong>Email:</strong> {app.email}</p>
                            <p><strong>National ID:</strong> {app.nationalId}</p>
                            <p><strong>Phone:</strong> {app.phoneNumber}</p>
                            <p><strong>Gender:</strong> {app.gender}</p>
                          </div>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-hec-100 text-hec-700 text-sm font-semibold rounded-full">
                        #{index + 1}
                      </span>
                    </div>

                    {/* Application Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <GraduationCap className="w-5 h-5 text-hec-600" />
                        <div>
                          <p className="text-xs text-gray-500">Current Level</p>
                          <p className="text-sm font-medium text-gray-900">{app.currentLevel || "N/A"}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <GraduationCap className="w-5 h-5 text-hec-600" />
                        <div>
                          <p className="text-xs text-gray-500">Apply Level</p>
                          <p className="text-sm font-medium text-gray-900">{app.applyLevel || "N/A"}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Award className="w-5 h-5 text-hec-600" />
                        <div>
                          <p className="text-xs text-gray-500">Academic Merit</p>
                          <p className="text-sm font-medium text-gray-900">{app.academicMerit || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-span-1 md:col-span-2 lg:col-span-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Field of Study</p>
                        <p className="text-sm font-medium text-gray-900">{app.fieldOfStudy || "N/A"}</p>
                      </div>
                      <div className="col-span-1 md:col-span-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Priority Course</p>
                        <p className="text-sm font-medium text-gray-900">{app.priorityCourse || "N/A"}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Priority Level</p>
                        <p className="text-sm font-medium text-gray-900">{app.priorityLevel || "N/A"}</p>
                      </div>
                    </div>

                    {/* Financial Details */}
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Financial Information</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-600">Tuition Fee</p>
                          <p className="text-sm font-semibold text-blue-700">{app.tuitionFee || "N/A"}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-green-600">Living Allowance</p>
                          <p className="text-sm font-semibold text-green-700">{app.monthlyLivingAllowance || "N/A"}</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <p className="text-xs text-purple-600">Research Allowance</p>
                          <p className="text-sm font-semibold text-purple-700">{app.researchAllowance || "N/A"}</p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <p className="text-xs text-orange-600">Air Ticket</p>
                          <p className="text-sm font-semibold text-orange-700">{app.airTicketAllowance || "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Marking Details */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Evaluation Marks</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-hec-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">
                                Criteria
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-hec-700 uppercase tracking-wider">
                                Marks Obtained
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-hec-700 uppercase tracking-wider">
                                Weight Obtained
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-hec-700 uppercase tracking-wider">
                                Comment
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {app.markings.map((marking, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{marking.criteriaName}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                                    {marking.marksObtained}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                                    {marking.weightObtained}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{marking.comment || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
